import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import {
  DEFAULT_ACCESS_CONTROL_SETTINGS,
  getAccessControlSettings,
} from "../../../shared/utils/accessControl";

function toNumber(value, fallback = 14) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function normalizeThaiPhone(phone) {
  const p = String(phone || "").replace(/\D/g, "");

  if (p.length === 10 && p.startsWith("0")) {
    return `+66${p.substring(1)}`;
  }

  return phone;
}

export default function AccessControlSettingsPage() {
  const { user, userDoc } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const [form, setForm] = useState(DEFAULT_ACCESS_CONTROL_SETTINGS);
  const [studentKeyword, setStudentKeyword] = useState("");
  const [studentLoading, setStudentLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [studentDevices, setStudentDevices] = useState([]);

  const isAdmin = (() => {
    const role = userDoc?.role;
    if (Array.isArray(role)) {
      return role.map((x) => String(x).toLowerCase()).includes("admin");
    }
    return String(role || "").toLowerCase() === "admin";
  })();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErrMsg("");
        const data = await getAccessControlSettings();
        setForm({
        ...DEFAULT_ACCESS_CONTROL_SETTINGS,
        ...(data || {}),
        });
      } catch (e) {
        setErrMsg(e?.message || "โหลดค่าการตั้งค่าไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    try {
      setSaving(true);
      setErrMsg("");
      setOkMsg("");

      await setDoc(
        doc(db, "systemSettings", "accessControl"),
        {
          ...form,
          deviceMonitorIntervalHours: toNumber(form.deviceMonitorIntervalHours, 6),
          deviceWarningThreshold: toNumber(form.deviceWarningThreshold, 2),
          deviceRiskThreshold: toNumber(form.deviceRiskThreshold, 3),
          enableDeviceMonitoring: !!form.enableDeviceMonitoring,
          enableDeviceMonitoringAdminOnly: !!form.enableDeviceMonitoringAdminOnly,
          enableDeviceWarningModal: !!form.enableDeviceWarningModal,
          enableCourseActiveDeviceLock: !!form.enableCourseActiveDeviceLock,
          enableCourseActiveDeviceLockAdminOnly:
            !!form.enableCourseActiveDeviceLockAdminOnly,
          courseActiveHeartbeatSeconds: toNumber(
            form.courseActiveHeartbeatSeconds,
            45
          ),
          courseActiveTimeoutSeconds: toNumber(
            form.courseActiveTimeoutSeconds,
            120
          ),
          desktopWindowDays: toNumber(form.desktopWindowDays, 14),
          tabletWindowDays: toNumber(form.tabletWindowDays, 14),
          mobileWindowDays: toNumber(form.mobileWindowDays, 14),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );

      setOkMsg("บันทึกการตั้งค่าเรียบร้อยแล้ว");
    } catch (e) {
      setErrMsg(e?.message || "บันทึกการตั้งค่าไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-5 text-center text-secondary">
        <Spinner animation="border" size="sm" /> กำลังโหลดการตั้งค่า...
      </div>
    );
  }

  if (!isAdmin) {
    return <Alert variant="danger">หน้านี้สำหรับผู้ดูแลระบบเท่านั้น</Alert>;
  }

  function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "string" || typeof v === "number") {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  } catch {}
  return null;
}

function formatThaiDateTime(v) {
  const d = toDateSafe(v);
  if (!d) return "-";
  return d.toLocaleString("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getRemainingText(lockedUntil) {
  const d = toDateSafe(lockedUntil);
  if (!d) return "-";

  const diff = d.getTime() - Date.now();
  if (diff <= 0) return "เปลี่ยนได้แล้ว";

  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `เหลือประมาณ ${days} วัน`;
}

const loadStudentDevices = async (uid) => {
  const snap = await getDocs(collection(db, "users", uid, "accessDevices"));
  setStudentDevices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
};

const searchStudent = async () => {
  try {
    setStudentLoading(true);
    setErrMsg("");
    setStudent(null);
    setStudentDevices([]);

    const keyword = studentKeyword.trim();
    if (!keyword) return;

    // 1) ลองค้นด้วย uid ตรง ๆ
    const userRef = doc(db, "users", keyword);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = { uid: userSnap.id, ...userSnap.data() };
      setStudent(data);
      await loadStudentDevices(userSnap.id);
      return;
    }

    // 2) ค้นด้วยเบอร์โทร
    const normalizedPhone = normalizeThaiPhone(keyword);

    const phoneCandidates = [
      keyword,
      normalizedPhone,
    ];

    let foundPhoneDoc = null;

    for (const phone of phoneCandidates) {
      const snap = await getDocs(
        query(
          collection(db, "users"),
          where("phoneNumber", "==", phone)
        )
      );

      if (!snap.empty) {
        foundPhoneDoc = snap.docs[0];
        break;
      }
    }

    if (foundPhoneDoc) {
      const data = {
        uid: foundPhoneDoc.id,
        ...foundPhoneDoc.data(),
      };

      setStudent(data);
      await loadStudentDevices(foundPhoneDoc.id);
      return;
    }

    // 3) ค้นด้วย email
    const emailSnap = await getDocs(
      query(collection(db, "users"), where("email", "==", keyword))
    );

    if (!emailSnap.empty) {
      const d = emailSnap.docs[0];
      const data = { uid: d.id, ...d.data() };
      setStudent(data);
      await loadStudentDevices(d.id);
      return;
    }

    setErrMsg("ไม่พบนักเรียนจาก UID / เบอร์โทร / อีเมลนี้");
  } catch (e) {
    setErrMsg(e?.message || "ค้นหาข้อมูลนักเรียนไม่สำเร็จ");
  } finally {
    setStudentLoading(false);
  }
};

const resetStudentDevice = async (slot) => {
  try {
    if (!student?.uid || !slot) return;
    const ok = window.confirm(`ยืนยันรีเซ็ตอุปกรณ์ประเภท ${slot} ของผู้เรียนนี้?`);
    if (!ok) return;

    await deleteDoc(doc(db, "users", student.uid, "accessDevices", slot));
    await loadStudentDevices(student.uid);
    setOkMsg(`รีเซ็ตอุปกรณ์ ${slot} เรียบร้อยแล้ว`);
  } catch (e) {
    setErrMsg(e?.message || "รีเซ็ตอุปกรณ์ไม่สำเร็จ");
  }
};

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">ตั้งค่าการเข้าถึงผู้ใช้งาน</h4>
          <div className="text-secondary small">
            ใช้กำหนดการล็อกอุปกรณ์และลายน้ำในหน้าเรียน
          </div>
        </div>

        <Button onClick={onSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              กำลังบันทึก...
            </>
          ) : (
            "บันทึกการตั้งค่า"
          )}
        </Button>
      </div>

      {okMsg ? <Alert variant="success">{okMsg}</Alert> : null}
      {errMsg ? <Alert variant="danger">{errMsg}</Alert> : null}

      <Row className="g-3">

        <Col lg={12}>
          <Card className="app-card">
            <Card.Body>
              <h5 className="mb-3">ระบบตรวจสอบอุปกรณ์แบบแจ้งเตือน</h5>

              <Form.Check
                type="switch"
                id="ac-device-monitor"
                label="เปิดใช้งานระบบตรวจสอบอุปกรณ์"
                checked={!!form.enableDeviceMonitoring}
                onChange={(e) =>
                  updateField("enableDeviceMonitoring", e.target.checked)
                }
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-device-monitor-admin-only"
                label="ทดสอบเฉพาะบัญชีแอดมิน"
                checked={!!form.enableDeviceMonitoringAdminOnly}
                
                onChange={(e) =>
                  updateField("enableDeviceMonitoringAdminOnly", e.target.checked)
                }
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-device-warning-modal"
                label="แสดง Modal แจ้งเตือนผู้ใช้งาน"
                checked={!!form.enableDeviceWarningModal}
                
                onChange={(e) =>
                  updateField("enableDeviceWarningModal", e.target.checked)
                }
                className="mb-3"
              />

              <div className="text-secondary small mb-3">
                ระบบนี้เป็นการบันทึกและแจ้งเตือนเท่านั้น ยังไม่บล็อก ไม่ logout
                และไม่ตัดสิทธิ์การเข้าเรียน
              </div>

              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>ตรวจสอบซ้ำทุกกี่ชั่วโมง</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.deviceMonitorIntervalHours}
                    
                    onChange={(e) =>
                      updateField("deviceMonitorIntervalHours", e.target.value)
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Label>เริ่มแจ้งเตือนเมื่อกี่เครื่อง</Form.Label>
                  <Form.Control
                    type="number"
                    min={2}
                    value={form.deviceWarningThreshold}
                    disabled={!form.enableDeviceMonitoring}
                    onChange={(e) =>
                      updateField("deviceWarningThreshold", e.target.value)
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Label>เสี่ยงสูงเมื่อกี่เครื่อง</Form.Label>
                  <Form.Control
                    type="number"
                    min={2}
                    value={form.deviceRiskThreshold}
                    disabled={!form.enableDeviceMonitoring}
                    onChange={(e) =>
                      updateField("deviceRiskThreshold", e.target.value)
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>


      </Row>

      <br/>

      <Row className="g-3">
        <Col lg={12}>
          <Card className="app-card">
            <Card.Body>
              <h5 className="mb-3">จำกัดการดูหน้าเรียนพร้อมกัน</h5>

              <Form.Check
                type="switch"
                id="ac-course-active-lock"
                label="เปิดระบบให้ 1 บัญชีดูหน้าเรียนได้ทีละ 1 อุปกรณ์"
                checked={!!form.enableCourseActiveDeviceLock}
                onChange={(e) =>
                  updateField("enableCourseActiveDeviceLock", e.target.checked)
                }
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-course-active-lock-admin-only"
                label="ทดสอบเฉพาะบัญชีแอดมิน"
                checked={!!form.enableCourseActiveDeviceLockAdminOnly}
                onChange={(e) =>
                  updateField("enableCourseActiveDeviceLockAdminOnly", e.target.checked)
                }
                className="mb-3"
              />

              <div className="text-secondary small mb-3">
                ระบบนี้มีผลเฉพาะหน้า CourseView เท่านั้น ไม่ logout ผู้ใช้
                และไม่กระทบหน้าอื่นของเว็บไซต์
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Heartbeat ทุกกี่วินาที</Form.Label>
                  <Form.Control
                    type="number"
                    min={15}
                    value={form.courseActiveHeartbeatSeconds}
                    onChange={(e) =>
                      updateField("courseActiveHeartbeatSeconds", e.target.value)
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>หมดอายุเมื่อไม่เจอ heartbeat กี่วินาที</Form.Label>
                  <Form.Control
                    type="number"
                    min={60}
                    value={form.courseActiveTimeoutSeconds}
                    onChange={(e) =>
                      updateField("courseActiveTimeoutSeconds", e.target.value)
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <br/>

      <Row className="g-3">
        <Col lg={6}>
          <Card className="app-card">
            <Card.Body>
              <h5 className="mb-3">การล็อกอุปกรณ์</h5>

              <Form.Check
                type="switch"
                id="ac-enabled"
                label="เปิดใช้งานระบบควบคุมการเข้าถึง"
                checked={!!form.enabled}
                onChange={(e) => updateField("enabled", e.target.checked)}
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-device-slots"
                label="เปิดใช้การผูกสิทธิ์ตามประเภทอุปกรณ์"
                checked={!!form.enforceDeviceSlots}
                onChange={(e) => updateField("enforceDeviceSlots", e.target.checked)}
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-admin-only-test-mode"
                label="โหมดทดสอบเฉพาะผู้ดูแลระบบเท่านั้น"
                checked={!!form.adminOnlyTestMode}
                onChange={(e) => updateField("adminOnlyTestMode", e.target.checked)}
                className="mb-3"
              />

              <Alert variant="info" className="small">
                หากเปิดโหมดนี้ ระบบจำกัดอุปกรณ์จะมีผลเฉพาะบัญชี admin เท่านั้น
                ผู้ใช้ทั่วไปจะยังเข้าเรียนได้ตามปกติ
              </Alert>

              {/* <Form.Check
                type="switch"
                id="ac-single-session"
                label="บังคับให้ 1 บัญชีใช้งานได้ทีละ 1 session"
                checked={!!form.singleSessionEnabled}
                onChange={(e) => updateField("singleSessionEnabled", e.target.checked)}
                className="mb-3"
              /> */}

              <Row className="g-3">
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    id="ac-allow-desktop"
                    label="Desktop"
                    checked={!!form.allowDesktop}
                    onChange={(e) => updateField("allowDesktop", e.target.checked)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    id="ac-allow-tablet"
                    label="Tablet"
                    checked={!!form.allowTablet}
                    onChange={(e) => updateField("allowTablet", e.target.checked)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    id="ac-allow-mobile"
                    label="Mobile"
                    checked={!!form.allowMobile}
                    onChange={(e) => updateField("allowMobile", e.target.checked)}
                  />
                </Col>
              </Row>

              <hr />

              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Desktop เปลี่ยนได้ทุกกี่วัน</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.desktopWindowDays}
                    onChange={(e) => updateField("desktopWindowDays", e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Tablet เปลี่ยนได้ทุกกี่วัน</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.tabletWindowDays}
                    onChange={(e) => updateField("tabletWindowDays", e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Mobile เปลี่ยนได้ทุกกี่วัน</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.mobileWindowDays}
                    onChange={(e) => updateField("mobileWindowDays", e.target.value)}
                  />
                </Col>
              </Row>

              <hr />

              <Form.Check
                type="switch"
                id="ac-block"
                label="ถ้าเป็นเครื่องใหม่ภายในช่วงเวลาที่ล็อกไว้ ให้บล็อกไว้ก่อน"
                checked={!!form.blockWhenMismatchWithinWindow}
                onChange={(e) =>
                  updateField("blockWhenMismatchWithinWindow", e.target.checked)
                }
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-confirm-replace"
                label="เมื่อครบกำหนดแล้ว ต้องกดยืนยันก่อนเปลี่ยนอุปกรณ์"
                checked={!!form.requireConfirmToReplaceAfterWindow}
                onChange={(e) =>
                  updateField("requireConfirmToReplaceAfterWindow", e.target.checked)
                }
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="app-card">
            <Card.Body>
              <h5 className="mb-3">ลายน้ำในวิดีโอ</h5>

              <Form.Check
                type="switch"
                id="ac-watermark"
                label="แสดงลายน้ำในหน้าเรียน"
                checked={!!form.showWatermark}
                onChange={(e) => updateField("showWatermark", e.target.checked)}
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-watermark-email"
                label="แสดงอีเมลแบบปกปิดบางส่วน"
                checked={!!form.watermarkShowEmail}
                disabled={!form.showWatermark}
                onChange={(e) => updateField("watermarkShowEmail", e.target.checked)}
                className="mb-3"
              />

              <Form.Check
                type="switch"
                id="ac-watermark-phone"
                label="แสดงเบอร์โทรแบบปกปิดบางส่วน"
                checked={!!form.watermarkShowPhoneMask}
                disabled={!form.showWatermark}
                onChange={(e) => updateField("watermarkShowPhoneMask", e.target.checked)}
                className="mb-3"
                />

                <Form.Check
                type="switch"
                id="ac-watermark-uid"
                label="แสดง UID ส่วนท้าย"
                checked={!!form.watermarkShowUidTail}
                disabled={!form.showWatermark}
                onChange={(e) => updateField("watermarkShowUidTail", e.target.checked)}
                />
            </Card.Body>
          </Card>
        </Col>
      </Row>


      <Card className="app-card mt-4">
        <Card.Body>
          <h5 className="mb-3">จัดการอุปกรณ์รายผู้เรียน</h5>

          <Row className="g-2 mb-3">
            <Col md={8}>
              <Form.Control
                placeholder="ค้นหาด้วย UID / เบอร์โทร / อีเมล"
                value={studentKeyword}
                onChange={(e) => setStudentKeyword(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Button
                className="w-100"
                variant="outline-primary"
                onClick={searchStudent}
                disabled={studentLoading}
              >
                {studentLoading ? "กำลังค้นหา..." : "ค้นหา"}
              </Button>
            </Col>
          </Row>

          {student ? (
            <>
              <Alert variant="secondary" className="small">
                <div><strong>UID:</strong> {student.uid}</div>
                <div><strong>อีเมล:</strong> {student.email || "-"}</div>
                <div><strong>เบอร์โทร:</strong> {student.phoneNumber || "-"}</div>
              </Alert>

              {studentDevices.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  ยังไม่มีการผูกอุปกรณ์ของผู้เรียนนี้
                </Alert>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {studentDevices.map((device) => (
                    <div
                      key={device.id}
                      className="border rounded-3 p-3 d-flex justify-content-between gap-3 flex-wrap"
                    >
                      <div>
                        <div className="fw-semibold">
                          {device.slot || device.id}
                        </div>
                        <div className="small text-secondary">
                          {device.currentLabel || "-"}
                        </div>
                        <div className="small text-secondary">
                          เปลี่ยนได้ตั้งแต่: {formatThaiDateTime(device.lockedUntil)}
                        </div>
                        <div className="small text-secondary">
                          สถานะ: {getRemainingText(device.lockedUntil)}
                        </div>
                        <div className="small text-secondary">
                          ใช้งานล่าสุด: {formatThaiDateTime(device.lastSeenAt)}
                        </div>
                      </div>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => resetStudentDevice(device.id)}
                      >
                        รีเซ็ตอุปกรณ์นี้
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
}